import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CreateRoleRequest, UpdateRoleRequest } from '@nucleus-commerce/api-client/admin';

import { teamApi } from '../api/team-api';

import { teamKeys } from './team-keys';

/** Role changes show up in member rows too (role names), so both lists refetch. */
function refreshTeam(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: teamKeys.roles() }),
    queryClient.invalidateQueries({ queryKey: teamKeys.members() }),
  ]);
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoleRequest) => teamApi.createRole(body),
    meta: { successMessage: 'team:roles.toasts.created', silentError: true },
    onSuccess: () => refreshTeam(queryClient),
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateRoleRequest }) =>
      teamApi.updateRole(id, body),
    meta: { successMessage: 'team:roles.toasts.updated', silentError: true },
    onSuccess: () => refreshTeam(queryClient),
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teamApi.removeRole(id),
    meta: { successMessage: 'team:roles.toasts.deleted', silentError: true },
    onSuccess: () => refreshTeam(queryClient),
  });
}
